import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { KeyRound } from 'lucide-react'
import { secretService, type SecretListItem } from '../services/api'

/**
 * * Lists secrets and the agents that carry them. The value stays hidden.
 */
export function Secrets() {
  const queryClient = useQueryClient()
  const { data: secrets, isLoading, isError } = useQuery('secrets', secretService.list)
  const [rotatingId, setRotatingId] = useState<string | null>(null)
  const [nextValue, setNextValue] = useState('')

  const rotateMutation = useMutation(
    ({ id, key }: { id: string; key: string }) => secretService.rotate(id, key),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('secrets')
        setRotatingId(null)
        setNextValue('')
      },
    }
  )
  const revokeMutation = useMutation(secretService.revoke, {
    onSuccess: () => queryClient.invalidateQueries('secrets'),
  })

  const confirmRevoke = (secret: SecretListItem) => {
    const names = secret.agents
      .map((agent) => agent.name || agent.containerUuid)
      .join(', ')
    const impact = names
      ? `Les agents suivants ne l'auront plus : ${names}.`
      : 'Aucun agent ne le porte actuellement.'
    if (window.confirm(`Révoquer ce secret ? ${impact}`)) {
      revokeMutation.mutate(secret.id)
    }
  }

  return (
    <div className="p-8">
      <h2 className="text-xl font-medium text-primary mb-2">Secrets</h2>
      <p className="text-secondary mb-6">
        Chaque secret garde un aperçu et la liste des agents qui le portent. La valeur n'est pas affichée.
      </p>
      {isLoading && <p className="text-secondary">Chargement…</p>}
      {isError && <p className="text-secondary">Impossible de charger les secrets.</p>}
      {!isLoading && !isError && (secrets ?? []).length === 0 && (
        <p className="text-secondary">Aucun secret enregistré.</p>
      )}
      <div className="space-y-3">
        {(secrets ?? []).map((secret) => (
          <div key={secret.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 font-medium text-primary">
                  <KeyRound className="w-4 h-4" />
                  <span>{secret.purpose}</span>
                  <span className="text-sm text-secondary">{secret.keyPreview}</span>
                </div>
                <p className="text-sm text-secondary mt-2">
                  {secret.agents.length === 0
                    ? 'Aucun agent'
                    : secret.agents.map((agent) => agent.name || 'Démarrage en cours').join(', ')}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => {
                    setRotatingId(secret.id)
                    setNextValue('')
                  }}
                >
                  Tourner
                </button>
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => confirmRevoke(secret)}
                >
                  Révoquer
                </button>
              </div>
            </div>
            {rotatingId === secret.id && (
              <form
                className="mt-4 flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault()
                  if (nextValue) {
                    rotateMutation.mutate({ id: secret.id, key: nextValue })
                  }
                }}
              >
                <input id={`secret-next-${secret.id}`} name="secret-next"
                  type="password"
                  className="input flex-1"
                  value={nextValue}
                  onChange={(event) => setNextValue(event.target.value)}
                  placeholder="Nouvelle valeur"
                  autoComplete="off"
                />
                <button type="submit" className="button button--primary" disabled={rotateMutation.isLoading}>
                  Enregistrer
                </button>
                <button
                  type="button"
                  className="button button--secondary"
                  onClick={() => setRotatingId(null)}
                >
                  Annuler
                </button>
              </form>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
